import type { BadgeOverviewItem, UserBadgeInstance } from '@/features/poules/api'

export type BadgeCategory = 'regular' | 'club'

export function isClubBadge(badge: BadgeOverviewItem) {
  if (normalizeString(badge.badge.category)?.toLowerCase() === 'club') return true
  if (badge.club?.id != null) return true
  return badge.earned_instances.some((instance) => hasBadgeClubContext(instance))
}

export function splitBadgesByCategory(items: BadgeOverviewItem[]) {
  return items.reduce(
    (groups, badge) => {
      if (isClubBadge(badge)) {
        groups.club.push(badge)
      } else {
        groups.regular.push(badge)
      }

      return groups
    },
    {
      regular: [] as BadgeOverviewItem[],
      club: [] as BadgeOverviewItem[],
    },
  )
}

export function getBadgeProgress(items: BadgeOverviewItem[]) {
  return {
    earned: items.filter((badge) => badge.earned).length,
    total: items.length,
  }
}

export function getBadgeDisplayImage(badge: BadgeOverviewItem) {
  if (isClubBadge(badge)) {
    const latestInstance = getLatestEarnedInstance(badge)
    return (
      normalizeString(badge.club?.logo) ??
      getMetaString(latestInstance?.meta, ['team_logo']) ??
      normalizeString(badge.badge.icon_url)
    )
  }

  return normalizeString(badge.badge.icon_url)
}

export function getLatestEarnedInstance(badge: BadgeOverviewItem) {
  return [...badge.earned_instances].sort(
    (left, right) => getTimeValue(right.earned_at) - getTimeValue(left.earned_at),
  )[0]
}

export function hasBadgeClubContext(instance: UserBadgeInstance) {
  return instance.club?.id != null || getMetaTeamId(instance.meta) != null
}

export function getMetaString(
  value: Record<string, unknown> | null | undefined,
  keys: string[],
) {
  if (!value) return undefined

  for (const key of keys) {
    const candidate = value[key]
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }

  return undefined
}

export function getMetaTeamId(value: Record<string, unknown> | null | undefined) {
  if (!value) return undefined

  const candidate = value.team_id
  if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate
  if (typeof candidate === 'string' && candidate.trim()) {
    const parsed = Number(candidate)
    if (Number.isFinite(parsed)) return parsed
  }

  return undefined
}

export function normalizeString(value?: string | null) {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

function getTimeValue(value?: string | null) {
  if (!value) return 0
  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}
